/**
 * =============================================================================
 *  TIM KHOI VE ANH TRONG CONTENT STREAM - va TU CHOI khi khong chac
 * =============================================================================
 *
 *  Mot anh da nhung duoc ve bang mot day toan tu, dang dien hinh la:
 *
 *      q                       <- luu trang thai do hoa
 *      120 0 0 80 60 500 cm    <- dat vi tri va kich thuoc
 *      /Im0 Do                 <- ve anh
 *      Q                       <- tra lai trang thai
 *
 *  Di chuyen anh = ghi lai sau con so cua `cm`. Xoa anh = cat bo ca doan tu `q`
 *  den `Q`.
 *
 * =============================================================================
 *  VI SAO TEP NAY CHU YEU LA CAC LY DO TU CHOI
 * =============================================================================
 *  Mot content stream hong lam HONG CA TRANG, doi khi ca tai lieu: trinh xem
 *  gap toan tu khong doc duoc thi bo phan con lai cua luong, va tat ca noi dung
 *  ve SAU cho ta cat nham se bien mat. Nguoi dung mat ca trang de doi lay mot
 *  buoc keo anh.
 *
 *  Vay nen can bang o day KHONG doi xung: mot tinh nang khong lam duoc thi
 *  nguoi dung con nhin thay ly do va con lam cach khac; mot trang bi xoa trang
 *  thi khong. Moi truong hop ta khong chac chan deu TU CHOI, kem ly do doc duoc.
 *
 *  Nam truong hop bi tu choi, va ca nam deu gap tren tai lieu that:
 *
 *    1. KHONG CO q/Q BAO NGOAI. `cm` luc do ap len phan con lai cua trang. Ghi
 *       lai no se lam moi thu ve sau anh xe dich theo; cat no di cung vay.
 *    2. KHONG CO `cm` trong khoi. Anh duoc ve bang CTM co san tu truoc do. Ta
 *       khong biet CTM do la gi neu chi nhin day byte, nen chen mot `cm` moi
 *       vao la doan bua.
 *    3. NHIEU `cm` KHONG LIEN TIEP. Nhieu `cm` lien tiep thi khong sao - chung
 *       nhan lai voi nhau va ta thay ca cum bang MOT toan tu duy nhat. Day la
 *       truong hop THUONG GAP chu khong hiem: pdf-lib phat ra ba `cm` lien nhau
 *       cho moi anh no ve (tinh tien, co gian, xoay), va nhieu bo sinh khac cung
 *       vay. Tu choi ca cum nay se lam tinh nang chet tren chinh nhung tep do
 *       ung dung nay tao ra.
 *       Nhung neu giua hai `cm` co mot toan tu khac (vi du `gs` dat do trong
 *       suot), thay ca doan bang mot `cm` se XOA MAT toan tu do - nen truong hop
 *       do bi tu choi.
 *    4. KHOI CON VE THU KHAC. Mot `q...Q` chua ca anh lan mot doan chu, hoac
 *       hai anh, thi cat bo khoi do se xoa luon phan khong lien quan.
 *    5. TOAN TU LE CAP. So `q` va `Q` khong khop la dau hieu luong bi cat hoac
 *       bo tach tu da lac duong. Khong dong vao nua.
 */
import type { PdfMatrix } from '@pdfeditor/types';
import { meaningful, tokenize, type Token } from './tokenizer';

/**
 * Toan tu duoc phep nam cung anh trong mot khoi q/Q.
 *
 * Deu la toan tu DAT TRANG THAI - chung khong ve gi ra giay, nen cat bo ca khoi
 * hay ghi lai `cm` deu khong lam mat noi dung nao khac. Danh sach nay la DANH
 * SACH TRANG (chi cai co ten moi duoc qua), khong phai danh sach den: mot toan
 * tu la khong biet chac lam gi thi phai bi coi la nguy hiem.
 */
const STATE_ONLY_OPS = new Set([
  'cm', 'gs', 'w', 'J', 'j', 'M', 'd', 'ri', 'i',
  'CS', 'cs', 'SC', 'SCN', 'sc', 'scn', 'G', 'g', 'RG', 'rg', 'K', 'k',
  'BMC', 'BDC', 'EMC', 'MP', 'DP',
]);

export interface ImageDrawBlock {
  /** Ten trong tu dien /XObject, khong co dau gach cheo. */
  name: string;
  /** Thu tu xuat hien cua toan tu `Do` nay trong luong. Bat dau tu 0. */
  occurrence: number;
  /** Vi tri byte cua `q` mo khoi. */
  blockStart: number;
  /** Vi tri byte ngay sau `Q` dong khoi. */
  blockEnd: number;
  /** Vi tri byte cua so dau tien cua `cm`, va ngay sau chu `cm`. */
  cmStart: number;
  cmEnd: number;
  /** Ma tran `cm` doc duoc - phan CUC BO, chua nhan voi phep bien doi bao ngoai. */
  localMatrix: PdfMatrix;
  /**
   * CO MOT DUONG CAT (`W`/`W*`) DANG CO HIEU LUC BAO NGOAI ANH NAY KHONG.
   *
   * Truong hop that, tim thay tren mot bao gia thuc te: cai logo o dau trang
   * duoc ve nhu sau
   *
   *     q  362.24 84 m ... h  W* n     <- cat dung bang o chu nhat cua logo
   *        q  ... cm  /Image1 Do  Q
   *     Q
   *
   * Doi mot chu `cm` o day la mot cai bay hoan hao: tep ghi ra van hop le, mo
   * lai van doc duoc, va pdf.js van bao ma tran da doi dung y muon - nhung
   * NGUOI DUNG THAY LOGO BIEN MAT, vi no vua bi keo ra ngoai chinh vung cat cua
   * no. Khong mot phep kiem nao dua tren cau truc luong hay tren ma tran bat
   * duoc loi nay; chi so sanh diem anh moi bat duoc.
   *
   * Nen di chuyen va co gian bi TU CHOI khi co cat. XOA thi van cho: cat bo ca
   * khoi ve lam anh bien mat, va do dung la thu nguoi dung yeu cau.
   */
  clipped: boolean;
}

/**
 * Ly do TU CHOI DI CHUYEN mot anh dang bi cat. Dung chung o ba noi - cua chan
 * cua loi, lop hien thi, va bo test - de ca ba noi khong the noi ba kieu khac
 * nhau ve cung mot han che.
 */
export const CLIPPED_IMAGE_REASON =
  'Ảnh này đang bị một đường cắt (clipping path) bao quanh, cắt đúng bằng khung hiện tại của nó. ' +
  'Di chuyển hay co giãn sẽ kéo ảnh ra ngoài chính vùng cắt đó và ảnh sẽ BIẾN MẤT khỏi trang in ' +
  'trong khi tệp vẫn mở được bình thường — nên thao tác này bị từ chối. Vẫn xóa được ảnh, và việc ' +
  'sửa đường cắt cần lõi Rust/PDFium.';

export interface RefusedImageDraw {
  name: string;
  occurrence: number;
  /** Ly do tieng Viet, hien thang cho nguoi dung. */
  reason: string;
}

export interface ImageScan {
  editable: ImageDrawBlock[];
  refused: RefusedImageDraw[];
}

/**
 * Quet mot content stream, tra ve moi lan ve anh kem ket luan sua duoc hay khong.
 *
 * `occurrence` dem theo TOAN TU `Do`, khong theo ten anh: mot anh duoc ve ba lan
 * la ba doi tuong rieng biet tren trang, va nguoi dung keo cai thu hai thi hai
 * cai kia phai dung yen.
 */
export function scanImageDraws(bytes: Uint8Array): ImageScan {
  const tokens = meaningful(tokenize(bytes));
  const editable: ImageDrawBlock[] = [];
  const refused: RefusedImageDraw[] = [];

  // Vi tri cua tung `q` dang mo, theo do sau.
  const stack: number[] = [];
  // DUONG CAT DANG CO HIEU LUC, song song voi `stack`.
  //
  // `clipAtDepth[k]` = da dat mot duong cat trong pham vi `q` thu k. Trong PDF,
  // duong cat bi gioi han boi q/Q y het moi trang thai do hoa khac: `Q` tra lai
  // vung cat cua muc tren. `clipAtBase` la duong cat dat NGOAI moi cap q - no
  // khong bao gio duoc tra lai, nen co hieu luc den het trang.
  const clipAtDepth: boolean[] = [];
  let clipAtBase = false;
  const clipInEffect = (): boolean => clipAtBase || clipAtDepth.some(Boolean);
  let occurrence = -1;
  let unbalanced = false;

  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i] as Token;
    if (t.kind !== 'operator') continue;

    if (t.text === 'q') {
      stack.push(i);
      clipAtDepth.push(false);
      continue;
    }
    if (t.text === 'Q') {
      if (stack.length === 0) unbalanced = true;
      else {
        stack.pop();
        clipAtDepth.pop();
      }
      continue;
    }
    // `W` va `W*` khong cat ngay: chung danh dau duong hien tai lam vung cat,
    // va phep cat co hieu luc sau toan tu ve duong ke tiep (thuong la `n`). Voi
    // muc dich o day - "anh nay co bi cat khong" - ghi nhan ngay tai `W` la du
    // va khong bo sot bien the nao.
    if (t.text === 'W' || t.text === 'W*') {
      if (clipAtDepth.length === 0) clipAtBase = true;
      else clipAtDepth[clipAtDepth.length - 1] = true;
      continue;
    }
    if (t.text !== 'Do') continue;

    occurrence++;
    const nameToken = tokens[i - 1];
    const name = nameToken && nameToken.kind === 'name' ? (nameToken.text ?? '') : '';
    if (!name) {
      refused.push({
        occurrence,
        name: '',
        reason: 'Toán tử Do không có tên XObject đứng trước. Luồng nội dung không đúng đặc tả.',
      });
      continue;
    }

    if (unbalanced) {
      refused.push({
        occurrence,
        name,
        reason:
          'Luồng nội dung của trang có toán tử q/Q lệch cặp trước chỗ vẽ ảnh này. ' +
          'Sửa vào một luồng đã lệch cặp có thể làm hỏng cả trang, nên thao tác bị từ chối.',
      });
      continue;
    }

    const openIndex = stack.length > 0 ? (stack[stack.length - 1] as number) : -1;
    if (openIndex < 0) {
      refused.push({
        occurrence,
        name,
        reason:
          'Ảnh này được vẽ mà không có cặp q/Q bao ngoài, nên ma trận đặt ảnh cũng đang chi phối ' +
          'phần nội dung vẽ sau nó. Di chuyển hay xóa ảnh sẽ kéo theo cả những thứ không liên quan. ' +
          'Cần lõi Rust/PDFium để tách riêng đối tượng này.',
      });
      continue;
    }

    const closeIndex = findClose(tokens, openIndex);
    if (closeIndex === null) {
      refused.push({
        occurrence,
        name,
        reason: 'Không tìm thấy toán tử Q đóng khối vẽ ảnh này. Luồng nội dung có thể bị cắt cụt.',
      });
      continue;
    }

    const inspected = inspectBlock(tokens, openIndex, closeIndex, i);
    if (typeof inspected === 'string') {
      refused.push({ occurrence, name, reason: inspected });
      continue;
    }

    editable.push({
      name,
      occurrence,
      blockStart: (tokens[openIndex] as Token).start,
      blockEnd: (tokens[closeIndex] as Token).end,
      cmStart: inspected.cmStart,
      cmEnd: inspected.cmEnd,
      localMatrix: inspected.matrix,
      clipped: clipInEffect(),
    });
  }

  return { editable, refused };
}

/** Vi tri cua `Q` khop voi `q` o `openIndex`, hoac null. */
function findClose(tokens: readonly Token[], openIndex: number): number | null {
  let depth = 0;
  for (let i = openIndex; i < tokens.length; i++) {
    const t = tokens[i] as Token;
    if (t.kind !== 'operator') continue;
    if (t.text === 'q') depth++;
    else if (t.text === 'Q') {
      depth--;
      if (depth === 0) return i;
    }
  }
  return null;
}

interface BlockShape {
  cmStart: number;
  cmEnd: number;
  matrix: PdfMatrix;
}

/** Nhan hai ma tran theo quy uoc VECTO HANG cua PDF: p x A x B. */
function multiply(a: PdfMatrix, b: PdfMatrix): PdfMatrix {
  return [
    a[0] * b[0] + a[1] * b[2],
    a[0] * b[1] + a[1] * b[3],
    a[2] * b[0] + a[3] * b[2],
    a[2] * b[1] + a[3] * b[3],
    a[4] * b[0] + a[5] * b[2] + b[4],
    a[4] * b[1] + a[5] * b[3] + b[5],
  ];
}

/**
 * Kiem tra khoi q...Q chi lam DUNG mot viec: dat ma tran roi ve mot anh.
 *
 * Tra ve chuoi = ly do tu choi.
 */
function inspectBlock(
  tokens: readonly Token[],
  openIndex: number,
  closeIndex: number,
  doIndex: number,
): BlockShape | string {
  const cms: { shape: BlockShape; tokenIndex: number }[] = [];
  let doCount = 0;

  for (let i = openIndex + 1; i < closeIndex; i++) {
    const t = tokens[i] as Token;

    if (t.kind === 'inline-image') {
      return (
        'Khối vẽ ảnh này còn chứa một ảnh nội dòng (BI/ID/EI). Cắt bỏ hoặc sửa khối sẽ ' +
        'làm mất cả ảnh nội dòng đó.'
      );
    }
    if (t.kind !== 'operator') continue;

    // q/Q long nhau ben trong: khoi khong con la mot don vi don gian nua.
    if (t.text === 'q' || t.text === 'Q') {
      return (
        'Khối vẽ ảnh này còn chứa các cặp q/Q lồng nhau, tức là nó bao nhiều đối tượng chứ ' +
        'không riêng ảnh. Cần lõi Rust/PDFium để tách riêng từng đối tượng.'
      );
    }

    if (t.text === 'Do') {
      doCount++;
      if (i !== doIndex) {
        return (
          'Cùng một khối q/Q vẽ nhiều đối tượng XObject. Di chuyển hay xóa sẽ ảnh hưởng ' +
          'tới cả những đối tượng còn lại trong khối.'
        );
      }
      continue;
    }

    if (t.text === 'cm') {
      const parsed = readMatrixBefore(tokens, i);
      if (!parsed) {
        return 'Toán tử cm của ảnh này không có đủ sáu con số đứng trước. Luồng nội dung không đúng đặc tả.';
      }
      cms.push({ shape: parsed, tokenIndex: i });
      continue;
    }

    if (!STATE_ONLY_OPS.has(t.text ?? '')) {
      return (
        `Khối vẽ ảnh này còn chứa toán tử "${t.text}", tức là nó vẽ thêm thứ khác ngoài ảnh. ` +
        'Sửa khối sẽ làm mất phần nội dung đó, nên thao tác bị từ chối.'
      );
    }
  }

  if (doCount === 0) {
    return 'Không xác định được toán tử vẽ ảnh trong khối.';
  }
  if (cms.length === 0) {
    return (
      'Ảnh này được vẽ bằng ma trận có sẵn từ trước chứ không có toán tử cm riêng, nên không ' +
      'xác định được đoạn nào quyết định vị trí của nó. Cần lõi Rust/PDFium.'
    );
  }

  const first = cms[0] as { shape: BlockShape; tokenIndex: number };
  const last = cms[cms.length - 1] as { shape: BlockShape; tokenIndex: number };

  // CAC `cm` PHAI LIEN TIEP NHAU. Giua chung chi duoc co con so (toan hang cua
  // chinh chung). Mot `gs` hay mot `rg` xen vao giua se bi xoa mat khi ta thay
  // ca doan bang mot toan tu duy nhat - xem ly do tu choi so 3 o dau tep.
  for (let i = first.tokenIndex + 1; i < last.tokenIndex; i++) {
    const t = tokens[i] as Token;
    if (t.kind === 'number') continue;
    if (t.kind === 'operator' && t.text === 'cm') continue;
    return (
      `Các toán tử cm của ảnh này bị toán tử "${t.text ?? t.kind}" xen vào giữa. Gộp chúng lại ` +
      'thành một sẽ xóa mất toán tử đó, nên thao tác bị từ chối.'
    );
  }

  // NHAN THEO THU TU NGUOC LAI thu tu xuat hien. `cm` nhan TRAI vao CTM dang
  // co: sau cm1 thi CTM = cm1 x CTM0, sau cm2 thi CTM = cm2 x cm1 x CTM0. Nen
  // ma tran cuc bo gop lai la cm_n x ... x cm_1. Nhan xuoi o day la mot loi
  // khong bao gio lo ra tren phep ti le va luon lo ra tren phep xoay.
  let composed: PdfMatrix = last.shape.matrix;
  for (let i = cms.length - 2; i >= 0; i--) {
    composed = multiply(composed, (cms[i] as { shape: BlockShape }).shape.matrix);
  }

  return { cmStart: first.shape.cmStart, cmEnd: last.shape.cmEnd, matrix: composed };
}

/** Sau con so ngay truoc toan tu `cm`. */
function readMatrixBefore(tokens: readonly Token[], cmIndex: number): BlockShape | null {
  const nums: number[] = [];
  let start = -1;
  for (let i = cmIndex - 1; i >= cmIndex - 6; i--) {
    const t = tokens[i];
    if (!t || t.kind !== 'number' || typeof t.value !== 'number') return null;
    nums.unshift(t.value);
    start = t.start;
  }
  if (nums.length !== 6 || start < 0) return null;
  const matrix = nums as unknown as PdfMatrix;
  return { cmStart: start, cmEnd: (tokens[cmIndex] as Token).end, matrix };
}

/**
 * THU TU CUA CAC LAN VE ANH, chi tinh nhung `Do` tro toi mot /Image.
 *
 * Tra ve mang: chi so trong mang la "anh thu may tren trang" theo cach pdf.js
 * dem, gia tri la so thu tu `Do` de tra nguoc ve luong noi dung.
 *
 * VI SAO CAN HAI HE DEM: `Do` cung ve ca Form XObject (mot trang con long
 * nhau), va pdf.js KHONG bao mot Form la mot lan ve - no trai phang noi dung
 * cua Form ra. Neu ta dem chung mot he, chi can trang co mot Form la moi anh
 * sau do bi lech mot bac, va nguoi dung keo anh A thi anh B nhay. Doi chieu hai
 * he dem la cua chan bat dieu do - xem matchesOperatorList().
 */
export function imageDrawOrder(scan: ImageScan, imageNames: ReadonlySet<string>): number[] {
  const all = [
    ...scan.editable.map((b) => ({ occurrence: b.occurrence, name: b.name })),
    ...scan.refused.map((r) => ({ occurrence: r.occurrence, name: r.name })),
  ].sort((a, b) => a.occurrence - b.occurrence);
  return all.filter((d) => imageNames.has(d.name)).map((d) => d.occurrence);
}

/** Khoi ve anh dung mot lan xuat hien cu the, hoac null. */
export function findDrawByOccurrence(
  scan: ImageScan,
  occurrence: number,
): ImageDrawBlock | null {
  return scan.editable.find((b) => b.occurrence === occurrence) ?? null;
}

/** Ly do tu choi cua mot lan xuat hien, hoac null neu no khong bi tu choi. */
export function refusalFor(scan: ImageScan, occurrence: number): string | null {
  return scan.refused.find((r) => r.occurrence === occurrence)?.reason ?? null;
}
